import * as Sentry from '@sentry/browser';
import * as SentryReact from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { Env, ILogger, Service } from '@bloomays-lib/types.shared';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import React from 'react';
let sentryInit = false;

export interface MonitoringOptions {
  sentryDSN?: string;
  useReplay: boolean;
  useReact: boolean;
  release: string;
  datadog: {
    datadogAPIKey?: string;
    datadogRUMAppId?: string;
    allowedTracingOrigins: string[];
  };
  env: Env;
  logger?: ILogger;
  service: Service;
}

const initMonitoring = (options: MonitoringOptions): void => {
  const sentryDSN = options.sentryDSN;
  const datadogAPIKey = options.datadog.datadogAPIKey;
  if (datadogAPIKey && options.datadog.datadogRUMAppId) {
    datadogRum.init({
      applicationId: options.datadog.datadogRUMAppId,
      clientToken: datadogAPIKey,
      site: 'datadoghq.eu',
      service: options.service,
      env: options.env,
      version: options.release,
      sampleRate: 100,
      trackInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingOrigins: options.datadog?.allowedTracingOrigins,
    });
    datadogRum.startSessionReplayRecording();
    options.logger?.info('Datadog RUM Init Done ✅');
  }

  const integrations = [];
  if (options.useReact) {
    integrations.push(
      SentryReact.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    );
  } else {
    integrations.push(
      Sentry.browserTracingIntegration({
        // trace sentry-trace header added to hubpost request in XHR
        traceXHR: false,
      }),
    );
  }
  if (options.useReplay) {
    integrations.push(Sentry.replayIntegration());
  }
  if (sentryDSN && !sentryInit) {
    Sentry.init({
      dsn: sentryDSN,
      integrations,
      tracePropagationTargets: ['localhost', /^.*\.bloomays\.com/],
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.1,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1.0,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 1.0,
      environment: options.env,
      release: options.release,
      beforeSend(event) {
        // Check if it is an exception, and if so, show the report dialog
        if (event.exception) {
          // Sentry.showReportDialog({ eventId: event.event_id })
        }
        return event;
      },
    });
    sentryInit = true;
    options.logger?.info(`Sentry Init (${integrations.map((i) => i.name).join(', ')}) Done ✅`);
  }
};

const setTransactionName = (transactionName: string | undefined): void => {
  Sentry.getCurrentScope().setTransactionName(transactionName || '');
};
const startTransaction = (name: string): any => {
  return Sentry.startSpan({ name }, (span) => {
    return span;
  });
};

const startStep = (transaction: any, stepName: string): any => {
  return Sentry.startSpan({ name: stepName, op: stepName }, (span) => {
    return span;
  });
};

// Note: stopStep and stopTransaction are no longer needed in Sentry v8
// Spans and transactions are automatically managed

const setUser = (user: { displayName: string; email: string }): void => {
  Sentry.setUser({
    id: user.email,
    email: user.email,
    username: user.displayName,
  });
  datadogRum.setUser({
    id: user?.email,
    name: user?.displayName,
    email: user?.email,
  });
};

const monitorException = (error: any, ctx?: any, flush?: boolean): void => {
  Sentry.captureException(error, ctx);
  if (flush === true) {
    Sentry.flush();
  }
};

const withScope = Sentry.withScope;

export { initMonitoring, setTransactionName, startTransaction, startStep, setUser, monitorException, withScope };
